import { ref, computed, ComputedRef, Ref } from 'vue';
import { debounce } from 'lodash-es';
import {
  PreparedSearchResultItem,
  PreparedItem,
  ItemsCacheKeys,
  StoredQueryOptions,
} from '@/models/item.models';
import { useRoot } from './component';
import { useItemStore } from '@/store/itemStore';
import { areFiltersApplied } from './filters';
import {
  PreparedGanttInitiative,
  PreparedGanttMilestone,
} from '@/models/gantt.models';
import { Folder, ItemType, WorkItem } from '@/api';

export function useItemSearch({
  searchType,
  filters,
  inclusionPredicate,
  debounceDelay = 400,
}: {
  searchType: ComputedRef<ItemType[]>;
  filters?: Ref<StoredQueryOptions>;
  /** true if item should be included in the search results. everything is included if this is not passed */
  inclusionPredicate?: (item: PreparedItem) => boolean;
  debounceDelay?: number;
}) {
  const root = useRoot();

  const queryModel = ref('');
  const search = ref<{
    loading: {
      items: boolean;
      folders: boolean;
    };
    result: {
      items: Array<PreparedSearchResultItem>;
      folders: Array<PreparedSearchResultItem>;
    };
    cursor: {
      item: string | null;
      folder: string | null;
    };
  }>({
    loading: {
      items: false,
      folders: false,
    },
    result: {
      items: [],
      folders: [],
    },
    cursor: {
      item: null,
      folder: null,
    },
  });

  function highlightResultText(text: string) {
    return text.replace(new RegExp(queryModel.value, 'gi'), (match) => {
      return '<span class="highlight-text">' + match + '</span>';
    });
  }

  const prepareSearchItems = (
    items: PreparedItem[]
  ): PreparedSearchResultItem[] => {
    return items
      .filter((item) => inclusionPredicate?.(item) ?? true)
      .map(
        (item): PreparedSearchResultItem => ({
          ...item,
          taggedNumber: highlightResultText(item.number),
          taggedTitle: highlightResultText(item.title),
        })
      );
  };

  const reset = (partial = false) => {
    if (partial) {
      search.value.loading = {
        items: false,
        folders: false,
      };
      search.value.cursor = {
        item: null,
        folder: null,
      };
    } else {
      queryModel.value = '';
      search.value = {
        loading: {
          items: false,
          folders: false,
        },
        result: {
          items: [],
          folders: [],
        },
        cursor: {
          item: null,
          folder: null,
        },
      };
    }
  };

  const SHORT_SEARCH_LIMIT = 3;
  const LONG_SEARCH_LIMIT = 10;

  async function searchItems() {
    const query = queryModel.value.trim();
    reset(true);
    if (!query && !areFiltersApplied(filters?.value || {})) return;

    const promises: Promise<unknown>[] = [];

    const limit =
      searchType.value.length === 1 ? LONG_SEARCH_LIMIT : SHORT_SEARCH_LIMIT;
    if (searchType.value.includes(ItemType.WORKITEM)) {
      search.value.loading.items = true;
      const pItems = root.$store.item
        .search({
          query: {
            ...filters?.value,
            q: query,
            itemType: ItemType.WORKITEM,
            limit,
          },
        })
        .then((res) => {
          search.value.result.items = prepareSearchItems(res.items);
          search.value.cursor.item = res.response?.meta.cursor ?? null;
          search.value.loading.items = false;
        });
      promises.push(pItems);
    }

    if (searchType.value.includes(ItemType.FOLDER)) {
      search.value.loading.folders = true;
      const pFolders = root.$store.item
        .search({
          query: {
            ...filters?.value,
            q: query,
            itemType: ItemType.FOLDER,
            limit,
          },
        })
        .then((res) => {
          search.value.result.folders = prepareSearchItems(res.items);
          search.value.cursor.folder = res.response?.meta.cursor ?? null;
          search.value.loading.folders = false;
        });
      promises.push(pFolders);
    }

    await Promise.all(promises);
  }
  async function searchMoreItems(itemType: ItemType) {
    const cursor = (() => {
      if (itemType === ItemType.FOLDER) {
        return search.value.cursor.folder;
      } else {
        return search.value.cursor.item;
      }
    })();
    if (!cursor) return;

    if (itemType === ItemType.FOLDER) {
      search.value.loading.folders = true;
    } else {
      search.value.loading.items = true;
    }

    const res = await root.$store.item.search({
      query: {
        ...filters?.value,
        q: queryModel.value.trim(),
        itemType,
        limit: LONG_SEARCH_LIMIT,
        cursor: cursor,
      },
    });
    if (itemType === ItemType.FOLDER) {
      search.value.result.folders.push(...prepareSearchItems(res.items));
      search.value.cursor.folder = res.response?.meta.cursor ?? null;
      search.value.loading.folders = false;
    } else {
      search.value.result.items.push(...prepareSearchItems(res.items));
      search.value.cursor.item = res.response?.meta.cursor ?? null;
      search.value.loading.items = false;
    }
  }
  const debouncedSearch = debounce(searchItems, debounceDelay);

  return {
    query: queryModel,
    isInitialLoading: computed(
      () =>
        (search.value.loading.items && !search.value.result.items.length) ||
        (search.value.loading.folders && !search.value.result.folders.length)
    ),
    isItemsLoading: computed(() => search.value.loading.items),
    isFoldersLoading: computed(() => search.value.loading.folders),
    result: computed(() => search.value.result),
    hasMoreItems: computed(() => !!search.value.cursor.item),
    hasMoreFolders: computed(() => !!search.value.cursor.folder),
    clear: reset,
    searchItems,
    searchMoreItems,
    debouncedSearch,
  };
}

/**
 * Fetches a list of item numbers efficiently using a combination of cache and batch fetching.
 * 0-2 requests are made for each call.
 *
 * Note 1: Fetched items are not fully populated (no descriptions). Should suffice for almost all cases.
 *
 * Note 2: Throws on error.
 */
export async function fetchItemListByNumbers({
  numbers,
  cacheKey,
  incrementalCallsLikely,
}: {
  numbers: (WorkItem['number'] | Folder['number'])[];
  cacheKey?: ItemsCacheKeys;
  /**
   * pass true if you're likely to call this function again with more numbers in the future.
   * eg: you will be mutating the list of numbers and calling this function again.
   * if true, the items will be cached in the store for future calls.
   * NOTE: if cacheKey is passed, this flag is ignored as the items are always cached.
   */
  incrementalCallsLikely?: boolean;
}): Promise<PreparedItem[]> {
  const itemStore = useItemStore();

  const itemsMap = new Map<PreparedItem['number'], PreparedItem>();
  const itemNumbersToLoadFromServer: PreparedItem['number'][] = [];
  const itemNumbersAlreadyInCache: PreparedItem['number'][] = [];
  const folderNumbersToLoadFromServer: PreparedItem['number'][] = [];
  const folderNumbersAlreadyInCache: PreparedItem['number'][] = [];

  numbers.forEach((number) => {
    const possiblyCachedItem = itemStore.loadFromCache(number);
    if (possiblyCachedItem) {
      const definitelyCachedItem = possiblyCachedItem;
      itemsMap.set(number, definitelyCachedItem);
      if (definitelyCachedItem.itemType === ItemType.WORKITEM)
        itemNumbersAlreadyInCache.push(number);
      else folderNumbersAlreadyInCache.push(number);
    } else {
      if (number.startsWith('F')) {
        folderNumbersToLoadFromServer.push(number);
      } else {
        itemNumbersToLoadFromServer.push(number);
      }
    }
  });

  const promises: Promise<void>[] = [];
  const cacheKeyToUse: ItemsCacheKeys | undefined = (() => {
    if (cacheKey) return cacheKey;
    if (incrementalCallsLikely)
      return ItemsCacheKeys.FETCH_ITEM_LIST_BY_NUMBERS;
    return undefined;
  })();

  if (itemNumbersToLoadFromServer.length) {
    promises.push(
      itemStore
        .search({
          query: {
            number: itemNumbersToLoadFromServer,
            itemType: ItemType.WORKITEM,
          },
          key: cacheKeyToUse,
        })
        .then((res) => {
          res.items.forEach((item) => itemsMap.set(item.number, item));
        })
    );
  }

  if (folderNumbersToLoadFromServer.length) {
    promises.push(
      itemStore
        .search({
          query: {
            number: folderNumbersToLoadFromServer,
            itemType: ItemType.FOLDER,
          },
          key: cacheKeyToUse,
        })
        .then((res) => {
          res.items.forEach((item) => itemsMap.set(item.number, item));
        })
    );
  }

  await Promise.all(promises);

  // add to cache if not already there
  if (
    cacheKey &&
    (itemNumbersAlreadyInCache.length || folderNumbersAlreadyInCache.length)
  ) {
    const existingItemNumbersInCacheKey = new Set<PreparedItem['number']>(
      itemStore.itemsCaches[cacheKey]?.map((item) => item.number) || []
    );
    const itemsToAdd: PreparedItem[] = [];

    [...itemNumbersAlreadyInCache, ...folderNumbersAlreadyInCache].forEach(
      (number) => {
        const item = itemsMap.get(number);
        if (!item || existingItemNumbersInCacheKey.has(number)) return;
        itemsToAdd.push(item);
      }
    );

    if (itemsToAdd.length)
      itemStore.addItems({
        items: itemsToAdd,
        keys: [cacheKey],
      });
  }

  const ret: PreparedItem[] = [];
  const missingItems: string[] = [];

  numbers.forEach((number) => {
    const item = itemsMap.get(number);
    if (!item) {
      missingItems.push(number);
    } else {
      ret.push(item);
    }
  });

  if (missingItems.length > 0) {
    console.warn(
      `Items not found: ${missingItems.join(
        ', '
      )}. They may have been deleted or you may not have permission to view them.`
    );
  }

  return ret;
}

export function isImportedItem(
  // to avoid null checks in the caller
  item:
    | PreparedItem
    | PreparedGanttInitiative
    | PreparedGanttMilestone
    | undefined
    | null
): boolean {
  return !!(
    item &&
    item.itemType === ItemType.WORKITEM &&
    (item.integrations.ado || item.integrations.jira)
  );
}

export function isIntegrationLinkedFolder(
  // to avoid null checks in the caller
  item:
    | PreparedItem
    | PreparedGanttInitiative
    | PreparedGanttMilestone
    | undefined
    | null
): boolean {
  return !!(
    item &&
    item.itemType === ItemType.FOLDER &&
    (item.integrations?.ado || item.integrations?.jira)
  );
}
